// Gera o PDF da transcrição a partir do mesmo HTML enviado por e-mail.
// O HTML é montado num iframe isolado (sem o CSS do painel), capturado com
// html2canvas e paginado em A4 com jsPDF. As quebras de página caem sempre
// entre linhas de texto, nunca no meio de uma.

// Layout da transcrição (640px) + margens laterais de ~16mm no A4
const RENDER_WIDTH = 760;
const RENDER_SCALE = 2;
// Limita a altura de cada captura para o canvas não passar do limite do navegador
const MAX_CAPTURE_HEIGHT = 4000;
const PAGE_WIDTH_MM = 210;
const PAGE_HEIGHT_MM = 297;
const PAGE_MARGIN_TOP_MM = 12;
// Embaixo fica o rodapé com protocolo, conversa e número da página
const PAGE_MARGIN_BOTTOM_MM = 20;
const FOOTER_LINE_MM = PAGE_HEIGHT_MM - 13;
const FOOTER_TEXT_MM = PAGE_HEIGHT_MM - 9;
const MM_PER_PX = PAGE_WIDTH_MM / RENDER_WIDTH;
const PAGE_HEIGHT = Math.floor(
  (PAGE_HEIGHT_MM - PAGE_MARGIN_TOP_MM - PAGE_MARGIN_BOTTOM_MM) / MM_PER_PX
);
const LINK_PROTOCOLS = ['http:', 'https:', 'mailto:'];
// Alguns glifos (vírgula, "g", "p") descem um pouco além da caixa da linha
const LINE_BOTTOM_SLACK = 2;
// Folga (px) entre a última linha e o fim da mensagem (margem do parágrafo + padding)
const MESSAGE_TAIL = 12;
// Imagem de 1px que o html2canvas usa para medir a linha de base das fontes
const HTML2CANVAS_PROBE_IMAGE =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const mountIframe = html =>
  new Promise(resolve => {
    const iframe = document.createElement('iframe');
    // Sem allow-scripts nada do HTML executa; allow-same-origin deixa o html2canvas ler o DOM
    iframe.setAttribute('sandbox', 'allow-same-origin');
    iframe.style.cssText = `position: fixed; top: 0; left: -${RENDER_WIDTH * 2}px; width: ${RENDER_WIDTH}px; border: 0;`;
    iframe.onload = () => resolve(iframe);
    iframe.srcdoc = html;
    document.body.appendChild(iframe);
  });

// O html2canvas mede a linha de base do texto no documento do painel (não no iframe),
// onde o CSS base (img { display: block }) desloca a medição e o texto sai desenhado
// mais abaixo do que o layout. Esta folha vale só durante a captura.
const fixFontMetricsProbe = () => {
  const sheet = new CSSStyleSheet();
  sheet.replaceSync(
    `body > div > img[src="${HTML2CANVAS_PROBE_IMAGE}"] { display: inline; }`
  );
  document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet];
  return () => {
    document.adoptedStyleSheets = document.adoptedStyleSheets.filter(
      item => item !== sheet
    );
  };
};

// Imagem de outro domínio sem CORS "contamina" o canvas e o navegador bloqueia a exportação
// do PDF inteiro (SecurityError). Imagem quebrada também não pode ser desenhada.
const blocksExport = image => {
  const context = document.createElement('canvas').getContext('2d');
  try {
    context.drawImage(image, 0, 0, 1, 1);
    context.getImageData(0, 0, 1, 1);
    return false;
  } catch (error) {
    return true;
  }
};

// Essas imagens são baixadas de novo como arquivo local (blob) ou, se não der, viram o
// texto alternativo: uma imagem problemática nunca derruba o PDF.
const makeImagesExportable = doc => {
  const objectUrls = [];
  const fixes = Array.from(doc.images)
    .filter(blocksExport)
    .map(async image => {
      try {
        const response = await fetch(image.src);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const url = URL.createObjectURL(await response.blob());
        objectUrls.push(url);
        image.src = url;
        await image.decode();
      } catch (error) {
        image.replaceWith(image.alt ? `[${image.alt}]` : '');
      }
    });
  return Promise.all(fixes).then(
    () => () => objectUrls.forEach(url => URL.revokeObjectURL(url))
  );
};

// Estica o iframe até a altura do conteúdo para a captura não depender de rolagem
const fitIframeToContent = iframe => {
  const root = iframe.contentDocument.documentElement;
  root.style.overflow = 'hidden';
  const height = root.scrollHeight;
  iframe.style.height = `${height}px`;
  return height;
};

// O html2canvas desenha os marcadores de lista acima da linha: troca-os por texto comum
const inlineListMarkers = doc => {
  doc.querySelectorAll('li').forEach(item => {
    const list = item.parentElement;
    const index = Array.prototype.indexOf.call(list.children, item);
    const start = Number(list.getAttribute('start')) || 1;
    item.style.listStyle = 'none';
    item.prepend(list.tagName === 'OL' ? `${start + index}. ` : '• ');
  });
};

// O rodapé do e-mail sai do fluxo e vira o rodapé de cada página, com a numeração
const takeFooter = doc => {
  const element = doc.querySelector('[data-transcript-footer]');
  if (!element) return null;

  const rect = element.getBoundingClientRect();
  element.style.display = 'none';
  return {
    text: element.textContent.trim(),
    pageLabel: element.dataset.pageLabel,
    left: rect.left * MM_PER_PX,
    right: rect.right * MM_PER_PX,
  };
};

// Pontos onde a página pode ser cortada: base de cada linha de texto, de cada imagem e
// de cada mensagem. Linhas em [data-keep-with-next] (autor e horário) descem junto com a
// mensagem, e a última linha da mensagem só sai junto com o fim dela: cortar ali deixaria
// a sobra da barra lateral no topo da página seguinte.
const collectBreakPoints = doc => {
  const points = [];
  const range = doc.createRange();
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    const node = walker.currentNode;
    const element = node.parentElement;
    if (!element.closest('[data-keep-with-next]')) {
      const message = element.closest('[data-message-body]');
      const messageBottom = message
        ? message.getBoundingClientRect().bottom
        : Infinity;
      range.selectNodeContents(node);
      Array.from(range.getClientRects())
        .filter(rect => messageBottom - rect.bottom > MESSAGE_TAIL)
        .forEach(rect => points.push(rect.bottom + LINE_BOTTOM_SLACK));
    }
  }
  doc.querySelectorAll('img, [data-message-body]').forEach(element => {
    points.push(element.getBoundingClientRect().bottom);
  });
  return points.map(Math.ceil).sort((a, b) => a - b);
};

// Último ponto de corte que ainda cabe na página; sem nenhum, corta no limite
const findPageEnd = (breakPoints, start, limit) =>
  breakPoints.filter(point => point > start && point <= limit).pop() || limit;

const paginate = (breakPoints, totalHeight) => {
  const contentBottom = breakPoints[breakPoints.length - 1] || totalHeight;
  const pages = [];
  let start = 0;
  while (start < contentBottom) {
    const limit = start + PAGE_HEIGHT;
    // A última página leva o que sobra do layout (borda do card, rodapé) até o
    // limite dela, para esse respiro final não virar uma página em branco
    const end =
      limit >= contentBottom
        ? Math.min(limit, totalHeight)
        : findPageEnd(breakPoints, start, limit);
    pages.push({ start, end });
    start = end;
  }
  return pages;
};

// Agrupa páginas vizinhas numa só captura, respeitando MAX_CAPTURE_HEIGHT
const groupPages = pages =>
  pages.reduce((groups, page) => {
    const group = groups[groups.length - 1];
    if (group && page.end - group.start <= MAX_CAPTURE_HEIGHT) {
      group.pages.push(page);
      group.end = page.end;
    } else {
      groups.push({ start: page.start, end: page.end, pages: [page] });
    }
    return groups;
  }, []);

const collectLinks = doc =>
  Array.from(doc.querySelectorAll('a[href]'))
    .filter(anchor => LINK_PROTOCOLS.includes(anchor.protocol))
    .flatMap(anchor =>
      Array.from(anchor.getClientRects()).map(rect => ({
        url: anchor.href,
        rect,
      }))
    );

const sliceCanvas = (canvas, offset, height) => {
  const slice = document.createElement('canvas');
  slice.width = canvas.width;
  slice.height = Math.round(height * RENDER_SCALE);
  slice
    .getContext('2d')
    .drawImage(canvas, 0, -Math.round(offset * RENDER_SCALE));
  return slice;
};

const addPageImage = (pdf, slice) => {
  // Pinta a página com o fundo do e-mail para as margens não destoarem
  const [red, green, blue] = slice
    .getContext('2d')
    .getImageData(0, 0, 1, 1).data;
  pdf.setFillColor(red, green, blue);
  pdf.rect(0, 0, PAGE_WIDTH_MM, PAGE_HEIGHT_MM, 'F');
  pdf.addImage(
    slice.toDataURL('image/jpeg', 0.8),
    'JPEG',
    0,
    PAGE_MARGIN_TOP_MM,
    PAGE_WIDTH_MM,
    (slice.height / RENDER_SCALE) * MM_PER_PX
  );
};

// A imagem não é clicável: recria os links (ex.: anexos) como áreas de link do PDF
const addPageLinks = (pdf, links, page) => {
  links
    .filter(({ rect }) => rect.top >= page.start && rect.top < page.end)
    .forEach(({ url, rect }) => {
      // Recorta no fim da página a área de links com padding (ex.: rodapé)
      const bottom = Math.min(rect.bottom, page.end);
      pdf.link(
        rect.left * MM_PER_PX,
        PAGE_MARGIN_TOP_MM + (rect.top - page.start) * MM_PER_PX,
        rect.width * MM_PER_PX,
        (bottom - rect.top) * MM_PER_PX,
        { url }
      );
    });
};

// Rodapé em texto (não imagem): fica nítido e o número da página é pesquisável
const addPageFooter = (pdf, footer, pageNumber, totalPages) => {
  const pageLabel = footer.pageLabel
    .replace('{page}', pageNumber)
    .replace('{total}', totalPages);
  pdf.setDrawColor(217, 219, 224);
  pdf.setLineWidth(0.2);
  pdf.line(footer.left, FOOTER_LINE_MM, footer.right, FOOTER_LINE_MM);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7.5);
  pdf.setTextColor(115, 115, 120);
  pdf.text(footer.text, footer.left, FOOTER_TEXT_MM);
  pdf.text(pageLabel, footer.right, FOOTER_TEXT_MM, { align: 'right' });
};

export const downloadTranscriptPdf = async (html, fileName) => {
  const [{ default: html2canvas }, { jsPDF: JsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ]);
  const iframe = await mountIframe(html);
  const removeProbeFix = fixFontMetricsProbe();
  let revokeImages = () => {};

  try {
    const doc = iframe.contentDocument;
    revokeImages = await makeImagesExportable(doc);
    inlineListMarkers(doc);
    const footer = takeFooter(doc);
    const totalHeight = fitIframeToContent(iframe);
    const pages = paginate(collectBreakPoints(doc), totalHeight);
    const links = collectLinks(doc);
    const pdf = new JsPDF({ unit: 'mm', format: 'a4', compress: true });
    pdf.setProperties({ title: doc.title });

    // Capturas em sequência: cada uma clona o documento e pesa na memória
    await groupPages(pages).reduce(async (previous, group) => {
      await previous;
      const canvas = await html2canvas(doc.body, {
        y: group.start,
        height: group.end - group.start,
        scale: RENDER_SCALE,
        useCORS: true,
        logging: false,
      });
      group.pages.forEach(page => {
        if (page.start > 0) pdf.addPage();
        addPageImage(
          pdf,
          sliceCanvas(canvas, page.start - group.start, page.end - page.start)
        );
        addPageLinks(pdf, links, page);
        if (footer) {
          addPageFooter(pdf, footer, pages.indexOf(page) + 1, pages.length);
        }
      });
    }, Promise.resolve());

    pdf.save(fileName);
  } finally {
    revokeImages();
    removeProbeFix();
    iframe.remove();
  }
};
